import { ReactNode, useLayoutEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';

/** 접힘(미리보기) 높이(px). 이보다 길면 "설명 더보기"로 펼친다. */
const PREVIEW_HEIGHT = 200;

type SeeMoreSectionProps = {
  title: string;
  children: ReactNode;
  isMobile?: boolean;
  /** 초기 펼침 여부(딥링크 등). 기본은 접힘. */
  defaultOpen?: boolean;
  /** 회색 배경(GREY_BG) 위에 놓일 때 하단 페이드 색을 배경과 맞춘다. 기본은 흰 배경. */
  onGrey?: boolean;
};

/**
 * 결제 페이지 대여규정과 동일한 "미리보기 + 설명 더보기" 익스팬더.
 * 접힘 상태에서 앞부분만 보여주고 하단 페이드 + "설명 더보기" 버튼으로 펼친다.
 * 내용이 미리보기 높이보다 짧으면 버튼/페이드 없이 전체를 노출한다.
 */
const SeeMoreSection = ({
  title,
  children,
  isMobile,
  defaultOpen = false,
  onGrey = false,
}: SeeMoreSectionProps) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const contentRef = useRef<HTMLDivElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  useLayoutEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    setIsOverflowing(el.scrollHeight > PREVIEW_HEIGHT + 8);
  }, [children, isMobile]);

  const collapsed = !isOpen && isOverflowing;

  return (
    <Wrapper isMobile={isMobile}>
      <Title isMobile={isMobile}>{title}</Title>
      <Content ref={contentRef} collapsed={collapsed} onGrey={onGrey}>
        {children}
      </Content>
      {isOverflowing && (
        <Toggle type='button' onClick={() => setIsOpen((prev) => !prev)}>
          {isOpen ? '접기' : '설명 더보기'}
          <Chevron open={isOpen} aria-hidden>
            <svg width='16' height='16' viewBox='0 0 16 16' fill='none'>
              <path
                d='M4 6l4 4 4-4'
                stroke='currentColor'
                strokeWidth='1.6'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </svg>
          </Chevron>
        </Toggle>
      )}
    </Wrapper>
  );
};

export default SeeMoreSection;

const Wrapper = styled.div<{ isMobile?: boolean }>`
  width: 100%;
  padding: ${({ isMobile }) => (isMobile ? '20px 0' : '28px 0')};
  border-bottom: 1px solid ${({ theme }) => theme.COLOR.GREY_05};
`;

const Title = styled.p<{ isMobile?: boolean }>`
  margin-bottom: 16px;
  ${({ isMobile, theme }) => (isMobile ? theme.FONT.H4 : theme.FONT.H3)};
  font-weight: 700;
  color: ${({ theme }) => theme.COLOR.GREY_01};
`;

const Content = styled.div<{ collapsed: boolean; onGrey: boolean }>`
  position: relative;
  overflow: hidden;
  max-height: ${({ collapsed }) =>
    collapsed ? `${PREVIEW_HEIGHT}px` : 'none'};

  ${({ collapsed, onGrey }) =>
    collapsed &&
    css`
      &::after {
        content: '';
        position: absolute;
        inset: auto 0 0 0;
        height: 84px;
        background: linear-gradient(
          to bottom,
          ${onGrey ? 'rgba(249, 249, 249, 0)' : 'rgba(255, 255, 255, 0)'},
          ${onGrey ? '#f9f9f9' : '#ffffff'}
        );
        pointer-events: none;
      }
    `}
`;

const Toggle = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  margin-top: 16px;
  padding: 14px 0;
  border: 1px solid ${({ theme }) => theme.COLOR.GREY_05};
  border-radius: ${({ theme }) => theme.BORDER_RADIUS.LEVEL_01};
  background-color: ${({ theme }) => theme.COLOR.WHITE};
  color: ${({ theme }) => theme.COLOR.GREY_01};
  ${({ theme }) => theme.FONT.B1};
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background-color: ${({ theme }) => theme.COLOR.GREY_BG};
  }
`;

const Chevron = styled.span<{ open: boolean }>`
  display: inline-flex;
  align-items: center;
  color: ${({ theme }) => theme.COLOR.GREY_02};
  transition: transform 0.2s ease;
  transform: rotate(${({ open }) => (open ? '180deg' : '0deg')});
`;
